/**
 * Client-side mirror of the backend's gibberish/repeat detection
 * (backend/services/contactSpamDetector.js). Used only for instant,
 * pre-submit UX feedback — the backend re-runs its own copy of this logic
 * independently and is the actual authority on what gets accepted.
 */

// Flags random/gibberish text using vowel ratio, longest consonant run, and
// mid-word case switching — patterns real words/names essentially never
// have. Text under 6 letters is skipped (too short to judge fairly).
export function looksLikeGibberish(text) {
  const letters = (text || '').replace(/[^a-zA-Z]/g, '');
  if (letters.length < 6) return false;

  const vowels = (letters.match(/[aeiouAEIOU]/g) || []).length;
  const vowelRatio = vowels / letters.length;

  let longestConsonantRun = 0;
  let currentRun = 0;
  for (const ch of letters) {
    if (!/[aeiouAEIOU]/.test(ch)) {
      currentRun++;
      longestConsonantRun = Math.max(longestConsonantRun, currentRun);
    } else {
      currentRun = 0;
    }
  }

  let caseChanges = 0;
  for (let i = 1; i < letters.length; i++) {
    const prevUpper = letters[i - 1] === letters[i - 1].toUpperCase();
    const curUpper = letters[i] === letters[i].toUpperCase();
    if (prevUpper !== curUpper) caseChanges++;
  }
  const caseChangeRatio = caseChanges / letters.length;

  return vowelRatio < 0.2 || longestConsonantRun >= 5 || caseChangeRatio > 0.35;
}

export function hasExcessiveRepeats(text) {
  return /(.)\1{4,}/.test(text || '');
}

// Checks word-by-word rather than the whole message at once, so a single
// borderline-vowel-light real word (e.g. "Thanks") isn't enough on its own —
// only multiple suspicious words, or the entire message being just one
// word, trips this.
export function hasGibberishWord(text) {
  const allTokens = (text || '').trim().split(/\s+/).filter(Boolean);
  // "The whole message is one blob" must be judged on the RAW token count —
  // not on how many tokens happen to be long enough to score, otherwise a
  // short normal sentence where only one word is 5+ letters (e.g. "Thanks
  // so much for your time.") gets misread as a single-word message.
  const isSingleWordMessage = allTokens.length === 1;

  const candidates = allTokens.filter((w) => w.replace(/[^a-zA-Z]/g, '').length >= 5);
  const suspicious = candidates.filter((w) => looksLikeGibberish(w) || hasExcessiveRepeats(w));

  if (isSingleWordMessage) return suspicious.length >= 1;
  return suspicious.length >= 2;
}
